import * as yup from 'yup';
import type { StoreDetailsInviteUserFormValues } from '../../../../types/features/storeDetails/users';
import {
    getDigitsOnly,
    isStoreDetailsInvitePinLoginAllowed,
    STORE_DETAILS_INVITE_LOGIN_METHODS,
    STORE_DETAILS_INVITE_USER_ROLES,
} from '../utils/storeDetailsInviteUserUtils';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const storeDetailsInviteUserSchema: yup.ObjectSchema<StoreDetailsInviteUserFormValues> = yup.object({
    email: yup.string().defined().test(
        'store-details-email-login',
        'Enter a valid email address',
        function (value) {
            const loginMethod = this.parent.loginMethod as StoreDetailsInviteUserFormValues['loginMethod'];
            const email = String(value || '').trim();

            if (loginMethod === 'email' && !email) {
                return this.createError({ message: 'Email address is required' });
            }

            return !email || emailPattern.test(email);
        },
    ),
    phone_number: yup.string().defined().test(
        'store-details-phone-login',
        'Phone number must be 10 digits',
        function (value) {
            const loginMethod = this.parent.loginMethod as StoreDetailsInviteUserFormValues['loginMethod'];
            const phoneDigits = getDigitsOnly(value);

            if (loginMethod === 'phone' && !phoneDigits) {
                return this.createError({ message: 'Phone number is required' });
            }

            return !phoneDigits || phoneDigits.length === 10;
        },
    ),
    name: yup.string().trim().required('Display name is required').min(2, 'Display name must be at least 2 characters'),
    role: yup.mixed<StoreDetailsInviteUserFormValues['role']>()
        .oneOf(STORE_DETAILS_INVITE_USER_ROLES)
        .required('Role is required'),
    loginMethod: yup.mixed<StoreDetailsInviteUserFormValues['loginMethod']>()
        .oneOf(STORE_DETAILS_INVITE_LOGIN_METHODS)
        .required('Login type is required'),
    cashier_mode_pin: yup.string().defined().test(
        'store-details-cashier-mode-pin',
        'PIN must be 6 digits',
        function (value) {
            const role = this.parent.role as StoreDetailsInviteUserFormValues['role'];
            const loginMethod = this.parent.loginMethod as StoreDetailsInviteUserFormValues['loginMethod'];
            const pinDigits = getDigitsOnly(value);

            if (loginMethod === 'pin' && !isStoreDetailsInvitePinLoginAllowed(role)) {
                return this.createError({ message: 'PIN login is only available for operators' });
            }

            if (loginMethod === 'pin' && !pinDigits) {
                return this.createError({ message: 'PIN is required' });
            }

            return !pinDigits || pinDigits.length === 6;
        },
    ),
    can_approve_reconciliation: yup.boolean().defined(),
});

export const STORE_DETAILS_INVITE_USER_DEFAULT_VALUES: StoreDetailsInviteUserFormValues = {
    email: '',
    phone_number: '',
    name: '',
    role: 'operator',
    loginMethod: 'phone',
    cashier_mode_pin: '',
    can_approve_reconciliation: false,
};
